import type { SheetViewProps } from "./Sheet";

export type SheetEnteringAnimationSettings = NonNullable<SheetViewProps["enteringAnimationSettings"]>;
export type SheetExitingAnimationSettings = NonNullable<SheetViewProps["exitingAnimationSettings"]>;

/** House sheet motion: the iOS modal-presentation curve. Duration is ms. */
export const SHEET_EASING = "cubic-bezier(0.32, 0.72, 0, 1)" as const;
export const SHEET_DURATION = 500;

/**
 * Backdrop fade over the full travel range. Silk's default backdrop animation
 * tops out at 0.33 opacity, which washes out the scrim tint the className
 * declares — this makes the class alpha the single authority.
 */
const BACKDROP_TRAVEL = { opacity: [0, 1] as [number, number] };

export interface SheetMotion {
  entering: SheetEnteringAnimationSettings;
  exiting: SheetExitingAnimationSettings;
  backdropTravel: typeof BACKDROP_TRAVEL;
}

/**
 * House entry/exit settings for isOpen surfaces.
 *
 * `preset` is NOT redundant alongside the easing. Silk 0.10.1 resolves View
 * animation settings as
 *
 *   { skip, easing: "spring", ...ours, ...(presetTable[ours.preset] ?? {}), ...(guard ? {} : vendorDefault) }
 *
 * and the guard passes only for a preset, `easing: "spring"`, or a *named* CSS
 * easing. A cubic-bezier matches none of those, so without a preset key the
 * vendor default is spread last and wins. Entering's default carries no
 * easing/duration and so leaves ours intact by accident; exiting's default is
 * `{ easing: "spring", … }` and silently replaces the house curve, after which
 * `duration` is discarded too. Naming a preset defeats that spread, and the
 * "smooth" table entry is spring parameters only — no easing, no duration — so
 * the curve below survives it. Re-verify the merge order in Silk's
 * dist/module.mjs on every upgrade; `motion.test.ts` pins the emitted object.
 *
 * Under reduced motion the content jumps to its destination while the backdrop
 * still cross-fades: `skip: true` would suppress travel entirely, and since the
 * scrim fade is travel-driven, a full-viewport black rectangle would hard-cut
 * in — the exact flash the setting exists to prevent. `skip` is reserved for
 * the story runner, whose teardown must never race a running animation.
 */
export function sheetMotion(reducedMotion: boolean, override?: { skip?: boolean } | null): SheetMotion {
  const settings = {
    contentMove: !reducedMotion,
    duration: SHEET_DURATION,
    easing: SHEET_EASING,
    preset: "smooth",
    skip: override?.skip ?? false,
  } as const;

  return { backdropTravel: BACKDROP_TRAVEL, entering: settings, exiting: settings };
}
